import { useEffect, useRef } from 'react';
import type { TaskEvent } from '../lib/types';

interface Props {
  events: TaskEvent[];
}

/**
 * Activity log — renders the pipeline's stdout like a terminal so the
 * user can see exactly what the agents are doing, line by line. Errors
 * and status transitions are styled to stand out.
 */
export default function EventsLog({ events }: Props) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to bottom on new events (terminal behavior).
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [events.length]);

  return (
    <div className="rounded border border-slate-200 bg-slate-900 text-slate-100">
      <div className="px-3 py-1.5 border-b border-slate-700 flex items-center justify-between">
        <h3 className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">
          Live log
        </h3>
        <span className="text-[10px] text-slate-500">{events.length} events</span>
      </div>
      <div
        ref={scrollerRef}
        className="h-[28rem] overflow-y-auto font-mono text-[11px] leading-snug p-3 space-y-0.5"
      >
        {events.length === 0 && (
          <div className="text-slate-500">No output yet — waiting for the worker…</div>
        )}
        {events.map((e) => (
          <EventLine key={e.id} event={e} />
        ))}
      </div>
    </div>
  );
}

function EventLine({ event }: { event: TaskEvent }) {
  const time = new Date(event.created_at).toLocaleTimeString();
  const p = (event.payload ?? {}) as Record<string, unknown>;

  // stdout = the meat. Render every line, stripped of ANSI codes.
  if (event.kind === 'stdout' && typeof p.text === 'string') {
    const lines = stripAnsi(p.text).split('\n').filter((l) => l.length > 0);
    if (lines.length === 0) return null;
    return (
      <>
        {lines.map((line, i) => (
          <div key={`${event.id}-${i}`} className="flex gap-2 whitespace-pre-wrap break-all">
            <span className="text-slate-600 shrink-0">{i === 0 ? time : ''.padEnd(time.length, ' ')}</span>
            <span className={lineColor(line)}>{line}</span>
          </div>
        ))}
      </>
    );
  }

  if (event.kind === 'status_update' && typeof p.status === 'string') {
    const status = String(p.status);
    const err = typeof p.error === 'string' ? p.error : '';
    return (
      <div className="flex gap-2 mt-1 mb-1">
        <span className="text-slate-600 shrink-0">{time}</span>
        <span className={statusColor(status)}>● status → {status}</span>
        {err && <span className="text-red-400">— {stripAnsi(err).slice(0, 240)}</span>}
      </div>
    );
  }

  if (event.kind === 'tokens_update') {
    const inp = Number(p.input_tokens) || 0;
    const out = Number(p.output_tokens) || 0;
    return (
      <div className="flex gap-2">
        <span className="text-slate-600 shrink-0">{time}</span>
        <span className="text-emerald-400">
          tokens: {inp.toLocaleString()} in / {out.toLocaleString()} out
        </span>
      </div>
    );
  }
  if (event.kind === 'cost_update') {
    // Suppressed in the UI now that we surface tokens. Backend still
    // stores cost_usd for historical reference.
    return null;
  }

  if (event.kind === 'pipeline_task_id') {
    const id = p.pipeline_task_id ? String(p.pipeline_task_id) : '';
    const br = p.branch ? String(p.branch) : '';
    return (
      <div className="flex gap-2">
        <span className="text-slate-600 shrink-0">{time}</span>
        <span className="text-sky-300">
          {id ? `id=${id}` : ''} {br ? `branch=${br}` : ''}
        </span>
      </div>
    );
  }

  if (event.kind === 'plan_ready') {
    return (
      <div className="flex gap-2">
        <span className="text-slate-600 shrink-0">{time}</span>
        <span className="text-amber-300">▸ plan_ready</span>
      </div>
    );
  }

  if (event.kind === 'final') {
    const code = p.exit_code as number | undefined;
    return (
      <div className="flex gap-2">
        <span className="text-slate-600 shrink-0">{time}</span>
        <span className={code === 0 ? 'text-emerald-400' : 'text-red-400'}>
          ▪ pipeline exited (code={code ?? '?'})
        </span>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <span className="text-slate-600 shrink-0">{time}</span>
      <span className="text-slate-500">{event.kind}</span>
    </div>
  );
}

/**
 * Strip ANSI escape codes (colors, bold, cursor moves) from a string.
 * The pipeline emits colorized output via picocolors; we render plain
 * text and re-apply color in CSS based on content.
 */
function stripAnsi(s: string): string {
  // eslint-disable-next-line no-control-regex
  return s.replace(/\[[0-9;]*[a-zA-Z]/g, '');
}

function lineColor(line: string): string {
  const t = line.trim();
  if (/^(✗|Error:|✗ task failed|Error |Failed:)/i.test(t)) return 'text-red-400';
  if (/^(✓|✔|Done)/.test(t)) return 'text-emerald-400';
  if (/^(▶|→|▸)/.test(t)) return 'text-sky-300';
  if (/^(⚠|Warning|warning:)/i.test(t)) return 'text-amber-300';
  return 'text-slate-200';
}

function statusColor(status: string): string {
  switch (status) {
    case 'completed': return 'text-emerald-400';
    case 'failed': return 'text-red-400';
    case 'cancelled': return 'text-slate-400';
    case 'pending_approval': return 'text-amber-300';
    case 'running': return 'text-sky-300';
    default: return 'text-slate-300';
  }
}
